/**
 * LessonPackPanel — composes one CURRENT pack into the Figma frame's
 * grid (`grid gap-8 lg:grid-cols-2 lg:gap-x-20`, gradient card left, ticket
 * details + instructors right). The only place that geometry lives, so the
 * page body and the history list don't each duplicate it.
 *
 * @module pages/lessons/components/LessonPackPanel
 */

import { useMemo } from 'react';
import { formatClubDate, asUtcDate, type Timezone } from '@butter/library/datetime';
import { TenantLink } from '~/lib/navigation';
import type { UserLesson } from '~/services/internalApi/lessons/types';
import { formatLessonHours } from '~/services/internalApi/lessons';
import {
  lessonPackBadgeLabel,
  isLessonPackSpendable,
  isLessonPackTombstoned,
  lessonPackCeilingMinutes,
  lessonPackName,
} from '@butter/library/lessons';
import { SubscriptionUsagePanel } from '@butter/ui/domains/subscriptions';
import { formatRemainingHeadline, lessonUsageRows } from '../lessonPackDisplay';
import { LessonPackCard } from './LessonPackCard';
import { LessonPackDetails } from './LessonPackDetails';
import { LessonInstructorList } from './LessonInstructorList';

export interface LessonPackPanelProps {
  pack: UserLesson;
  timezone: Timezone;
}

export function LessonPackPanel({ pack, timezone }: LessonPackPanelProps) {
  // Never a plain Date, never a plain string timezone — wrap at the boundary.
  const purchasedLabel = formatClubDate(asUtcDate(new Date(pack.purchaseDate)), timezone, 'MMM d, yyyy');
  const includedLabel = formatLessonHours(lessonPackCeilingMinutes(pack));
  // Spendability, not the status column: a replaced pack with restored minutes
  // stays `used` on the row but pays for a lesson, so it reads Active.
  const statusLabel = lessonPackBadgeLabel(pack);
  const spendable = isLessonPackSpendable(pack);
  const usageRows = useMemo(() => lessonUsageRows(pack), [pack]);

  return (
    <div className="grid gap-8 lg:grid-cols-2 lg:gap-x-20 lg:items-start">
      <LessonPackCard
        packId={pack.id}
        name={lessonPackName(pack)}
        description={pack.lesson?.description}
        colorName={pack.lesson?.color}
        statusLabel={statusLabel}
        figureLabel={formatRemainingHeadline(pack.remainingMinutes)}
        purchasedLabel={purchasedLabel}
        isTombstoned={isLessonPackTombstoned(pack)}
      />
      <div className="flex flex-col gap-8">
        <LessonPackDetails
          packId={pack.id}
          purchasedLabel={purchasedLabel}
          includedLabel={includedLabel}
        />
        {spendable && (
          <div data-testid={`lessons-pack-usage-${pack.id}`}>
            <SubscriptionUsagePanel
              collapsible={false}
              rows={usageRows}
              title={lessonPackName(pack)}
              compact
              barColor="var(--theme-600)"
              barVariant="continuous"
            />
          </div>
        )}
        {/* Spendable = active + scheduled (@butter/library/lessons); history
            packs never reach this panel. */}
        {spendable && <LessonInstructorList instructors={pack.allowedInstructors} packId={pack.id} />}
        {spendable && (
          <TenantLink
            to="/lessons/book"
            data-testid={`lesson-pack-book-link-${pack.id}`}
            className="flex h-12 w-full items-center justify-center rounded-full bg-theme-600 text-base font-semibold text-white text-interactive:bg-theme-700 lg:h-16 lg:text-xl"
          >
            Book a Lesson
          </TenantLink>
        )}
      </div>
    </div>
  );
}
